/**
 * Hook to register Item Price tab with DetailPageTabsContext
 * 
 * This hook automatically adds the Item Price tab to Item doctype detail pages only.
 * 
 * Best Practices:
 * - Uses functional updates for setTabs (prev => ...) to avoid dependency issues
 * - No dependency on tabs array (prevents loops/glitches)
 * - Automatically manages tab lifecycle (add/remove based on conditions)
 * - Uses constant for tab ID to ensure consistency
 * - Only shows for Item doctype
 */

import { useEffect } from 'react';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { useLanguage } from '@/i18n/useLanguage';
import { getTabLabelTranslation } from '@/lib/views/labels/translations';
import { DASHBOARD_TAB_ID } from '@/features/common/doc-dashboard/useDashboardTab';

/**
 * Tab ID constant - use this everywhere you reference the Item Price tab
 */
export const ITEM_PRICE_TAB_ID = 'item-price';

interface UseItemPriceTabOptions {
  doctype: string;
  name?: string;
  enabled?: boolean; // Only show for existing documents
}

/**
 * Hook to register Item Price tab with DetailPageTabsContext.
 * 
 * This makes the Item Price tab available in the tab navigation
 * for Item doctype detail pages only.
 * 
 * The tab is automatically:
 * - Added when document has a name (not new) and doctype is "Item"
 * - Removed when document is new or doctype is not "Item"
 * - Positioned after Dashboard tab
 * 
 * Uses functional updates to prevent dependency issues and tab duplication.
 * 
 * ⚠️ GUARDRAIL: Never add a "watchdog" effect that re-adds this tab if it disappears.
 * If tabs are being removed unexpectedly, fix the tab merging logic in SystemView/ExperienceView
 * or TabRenderer that's causing the removal. A watchdog effect creates tab thrash, duplicates,
 * weird ordering, and hard-to-debug behavior.
 */
export function useItemPriceTab({ 
  doctype, 
  name, 
  enabled = true 
}: UseItemPriceTabOptions) {
  const { setTabs } = useDetailPageTabs();
  const { language } = useLanguage();
  const itemPriceLabel = getTabLabelTranslation("Item Price", language) || 'Item Price';
  
  useEffect(() => {
    // Only show for Item doctype
    const isItemDoctype = doctype === 'Item';
    const shouldShow = enabled && isItemDoctype && !!name && name !== 'new';
    
    setTabs((prevTabs: TabStructure[]) => {
      const exists = prevTabs.some(t => t.name === ITEM_PRICE_TAB_ID);
      
      // Debug logging (dev only) - use prevTabs instead of contextTabs to avoid dependency
      if (import.meta.env.DEV) {
        console.log('[useItemPriceTab]', {
          doctype,
          name,
          enabled,
          isItemDoctype,
          shouldShow,
          currentTabsCount: prevTabs.length,
          hasItemPriceTab: exists,
        });
      }
      
      // Create the Item Price tab structure
      const itemPriceTab: TabStructure = {
        name: ITEM_PRICE_TAB_ID,
        label: itemPriceLabel,
        sections: [], // Empty sections - this tab is handled separately in SystemView/ExperienceView
      };
      
      // If tab should show
      if (shouldShow) {
        // If tab doesn't exist, add it
        if (!exists) {
          // Find Dashboard tab index to insert after it
          const dashboardIdx = prevTabs.findIndex(t => t.name === DASHBOARD_TAB_ID);
          
          if (dashboardIdx !== -1) {
            // Insert after Dashboard tab
            const newTabs = [...prevTabs];
            newTabs.splice(dashboardIdx + 1, 0, itemPriceTab);
            if (import.meta.env.DEV) {
              console.log('[useItemPriceTab] Added Item Price tab after Dashboard', {
                tabName: ITEM_PRICE_TAB_ID,
                totalTabs: newTabs.length,
                tabLabels: newTabs.map(t => t.label),
              });
            }
            return newTabs;
          }
          
          // If Dashboard tab doesn't exist, append at end
          const result = [...prevTabs, itemPriceTab];
          if (import.meta.env.DEV) {
            console.log('[useItemPriceTab] Added Item Price tab at end', {
              tabName: ITEM_PRICE_TAB_ID,
              totalTabs: result.length,
              tabLabels: result.map(t => t.label),
            });
          }
          return result;
        }
        
        // Tab exists - ensure it has correct label
        const currentIdx = prevTabs.findIndex(t => t.name === ITEM_PRICE_TAB_ID);
        if (currentIdx !== -1) {
          const currentTab = prevTabs[currentIdx];
          if (currentTab.label !== itemPriceLabel) {
            const newTabs = [...prevTabs];
            newTabs[currentIdx] = { ...currentTab, label: itemPriceLabel };
            return newTabs;
          }
        }
        
        // Tab exists and is correct - no change needed
        return prevTabs;
      }
      
      // Tab should not show - remove it if it exists
      if (!shouldShow && exists) {
        if (import.meta.env.DEV) {
          console.log('[useItemPriceTab] Removing Item Price tab (conditions not met)');
        }
        return prevTabs.filter(t => t.name !== ITEM_PRICE_TAB_ID);
      }
      
      // No change needed
      return prevTabs;
    });
  }, [doctype, name, enabled, setTabs, itemPriceLabel]);
  
  return {
    tabId: ITEM_PRICE_TAB_ID,
    isEnabled: enabled && doctype === 'Item' && !!name && name !== 'new',
  };
}

